import type { MapRef } from "@vis.gl/react-maplibre";
import { RefObject, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { getAliveMap } from "./maplibre-map.utils";

/**
 * Keeps the built-in maplibre-gl control tooltips (zoom + fullscreen) in the
 * current app language. The map `locale` option is init-only — maplibre-gl has no
 * `setLocale` — so on a language switch the control button `title`/`aria-label`
 * are patched directly; otherwise they stay in the boot language until a refresh.
 * Re-applied on fullscreen toggle, which rewrites its own title from the boot locale.
 */
export const useMapLibreControlTooltips = (
  mapRef: RefObject<MapRef | null>,
  isMapLoaded: boolean,
): void => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const map = getAliveMap(mapRef.current);
    if (!map || !isMapLoaded) {
      return;
    }

    const container = map.getContainer();
    const tooltips: [string, string][] = [
      [".maplibregl-ctrl-zoom-in", t("general.zoomIn")],
      [".maplibregl-ctrl-zoom-out", t("general.zoomOut")],
      [".maplibregl-ctrl-fullscreen", t("general.enterFullscreen")],
      [".maplibregl-ctrl-shrink", t("general.exitFullscreen")],
    ];

    const applyTooltips = (): void => {
      tooltips.forEach(([selector, title]) => {
        const button = container.querySelector<HTMLButtonElement>(selector);
        if (button) {
          button.title = title;
          button.setAttribute("aria-label", title);
        }
      });
    };

    applyTooltips();

    // The fullscreen control rewrites its own title from the boot locale on every
    // toggle — re-apply after the DOM settles so the localized title wins.
    const handleFullscreenChange = (): void => {
      window.requestAnimationFrame(applyTooltips);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [t, i18n.language, isMapLoaded, mapRef]);
};
